import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getServiceCategoryBySlug } from "../../constants/service-categories";

function stripTrailingSlash(pathname) {
  const p = String(pathname || "");
  if (!p || p === "/") return "/";
  return p.endsWith("/") ? p.slice(0, -1) : p;
}

function removeEnPrefix(pathname) {
  const p = String(pathname || "");
  if (p === "/en") return "/";
  if (p.startsWith("/en/")) return p.slice("/en".length) || "/";
  return p || "/";
}

function addEnPrefix(pathname) {
  const base = String(pathname || "/") || "/";
  const clean = base === "/" ? "" : base;
  return `/en${clean}`;
}

function sanitizeTitle(input) {
  const t = String(input || "").trim();
  if (!t) return t;
  return t
    .replace(/\s*[—-]\s*www\.softindex\.ro\s*$/i, "")
    .replace(/\s*[—-]\s*softindex\.ro\s*$/i, "")
    .trim();
}

function normalizeSiteUrl(input) {
  const v = String(input || "")
    .trim()
    .replace(/\/+$/, "");
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
}

const SITE_URL =
  normalizeSiteUrl(process.env.REACT_APP_PUBLIC_SITE_URL) ||
  "https://www.softindex.ro";

const STATIC_INDEX_PATHS = new Set([
  "/services",
  "/portfolio",
  "/contact",
  "/privacy-policy",
  "/terms-and-conditions",
]);

function normalizePathname(pathname) {
  const p = pathname || "/";
  if (p === "") return "/";
  if (p !== "/" && p.endsWith("/")) {
    return p.slice(0, -1) || "/";
  }
  return p;
}

function isPublicIndexablePath(pathname) {
  const p = normalizePathname(pathname);
  if (p === "/") return true;
  if (STATIC_INDEX_PATHS.has(p)) return true;
  if (p.startsWith("/services/")) {
    const slug = p.slice("/services/".length);
    return Boolean(getServiceCategoryBySlug(slug));
  }
  return false;
}

const GSC_VERIFICATION = process.env.REACT_APP_GSC_VERIFICATION;

const Meta = ({
  title = "Softindex | Software web & mobile la comandă",
  description = "Softindex dezvoltă software la comandă pentru companii: site-uri personalizate, aplicații web și mobile, integrări și mentenanță. Livrăm cu focus pe performanță, SEO, securitate și rezultate măsurabile, de la idee la lansare.",
  image = `${SITE_URL}/og-image.png`,
  url,
  canonical,
  structuredData = null,
  siteName = "Softindex",
  imageAlt,
  locale: localeProp,
  alternateLocales: alternateLocalesProp,
  robots,
  htmlLang: htmlLangProp,
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const htmlLang =
    htmlLangProp ?? (i18n.language?.startsWith("ro") ? "ro" : "en");
  const locale =
    localeProp ?? (i18n.language?.startsWith("ro") ? "ro_RO" : "en_US");
  const alternateLocales =
    alternateLocalesProp ??
    (i18n.language?.startsWith("ro") ? ["en_US"] : ["ro_RO"]);
  const rawPathname = location.pathname || "/";
  const pathname = stripTrailingSlash(rawPathname);
  const roPath = stripTrailingSlash(removeEnPrefix(pathname));
  const enPath = stripTrailingSlash(addEnPrefix(roPath));
  const resolvedRobots =
    robots ??
    (isPublicIndexablePath(pathname) ? "index, follow" : "noindex, follow");
  const canonicalPath = pathname;
  const defaultUrl =
    canonicalPath === "/" ? `${SITE_URL}/` : `${SITE_URL}${canonicalPath}`;
  const fullUrl = canonical || url || defaultUrl;
  const canonicalHref = fullUrl;
  const roHref = roPath === "/" ? `${SITE_URL}/` : `${SITE_URL}${roPath}`;
  const enHref = enPath === "/en" ? `${SITE_URL}/en` : `${SITE_URL}${enPath}`;

  const cleanTitle = sanitizeTitle(title);
  const ogImageAlt = imageAlt || cleanTitle;
  const showOgAlternate =
    Array.isArray(alternateLocales) && alternateLocales.length > 0 && locale;

  useEffect(() => {
    document.documentElement.lang = htmlLang;
  }, [htmlLang]);

  return (
    <>
      <title>{cleanTitle}</title>
      <meta name="description" content={description} />
      <meta name="application-name" content={siteName} />
      <meta name="robots" content={resolvedRobots} />
      <link rel="canonical" href={canonicalHref} />
      <link rel="alternate" hrefLang="ro" href={roHref} />
      <link rel="alternate" hrefLang="en" href={enHref} />
      <link rel="alternate" hrefLang="x-default" href={roHref} />
      {GSC_VERIFICATION ? (
        <meta name="google-site-verification" content={GSC_VERIFICATION} />
      ) : null}

      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={cleanTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalHref} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={locale} />
      {showOgAlternate
        ? alternateLocales.map((loc) => (
            <meta key={loc} property="og:locale:alternate" content={loc} />
          ))
        : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:domain" content="www.softindex.ro" />
      <meta name="twitter:url" content={canonicalHref} />
      <meta name="twitter:title" content={cleanTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {structuredData ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      ) : null}
    </>
  );
};

export default Meta;
export { SITE_URL };
